import { db, storage } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

interface LogoData {
  url: string;
  alt: string;
  width?: number;
  height?: number;
  updatedAt: string;
}

class LogoService {
  private readonly COLLECTION = 'settings';
  private readonly DOC_ID = 'logo';
  private readonly STORAGE_PATH = 'logos';

  async getLogo(): Promise<LogoData | null> {
    try {
      const docRef = doc(db, this.COLLECTION, this.DOC_ID);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      return docSnap.data() as LogoData;
    } catch (error) {
      console.error('Error fetching logo:', error);
      return null;
    }
  }

  async updateLogo(file: File, alt: string, width?: number, height?: number): Promise<void> {
    try {
      // Upload file to storage
      const timestamp = Date.now();
      const filename = `logo_${timestamp}_${file.name}`;
      const storageRef = ref(storage, `${this.STORAGE_PATH}/${filename}`);
      
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      // Delete old logo if it exists
      const oldData = await this.getLogo();
      if (oldData?.url && oldData.url.includes('firebase')) {
        try {
          // Extract the path from the URL
          const oldUrl = new URL(oldData.url);
          const pathMatch = oldUrl.pathname.match(/o\/(.+?)(?:\?|$)/);
          if (pathMatch && pathMatch[1]) {
            const oldPath = decodeURIComponent(pathMatch[1]);
            const oldRef = ref(storage, oldPath);
            await deleteObject(oldRef);
          }
        } catch (error) {
          console.warn('Error deleting old logo:', error);
        }
      }

      // Update Firestore document
      const data = {
        url,
        alt: alt || 'Ridgeline Homes Logo',
        width: width || 200,
        height: height || 50,
        updatedAt: new Date().toISOString()
      };
      
      const docRef = doc(db, this.COLLECTION, this.DOC_ID);
      await setDoc(docRef, data);
    } catch (error) {
      console.error('Error updating logo:', error);
      throw error;
    }
  }
}

export const logoService = new LogoService(); 